// ============================================================
// FILE: _services/aabo-scope/scope-manager.service.ts
//
// Ported from EzovionOPDFrontend (Angular 20) core/services/aabo-scope/
// scope-manager.service.ts -- talks to the local scope-bridge Node process
// (see the sibling AaboScopeBridge project's bridge-server.js) over a plain
// WebSocket, rather than importing the vendor Aabo Scope SDK directly the
// way X6ManagerService imports aabo-sdk.js. The Scope SDK is Node-only --
// it opens a raw UDP socket to the device and does OS-level WiFi
// scan/connect via node-wifi, neither of which a browser can do. The
// bridge runs the real SDK and relays everything here over WebSocket
// (JPEG frames as binary messages, JSON for status/commands) -- protocol
// matches the bridge's own header comment exactly; see that file before
// changing either side.
//
// Only the environment import path was adjusted to this app's folder
// depth, and the WifiNetwork/ScopeConnectionStatus/ScopeState types now
// live in the shared device-integration model file instead of being
// declared inline here -- source already used constructor DI
// (constructor(private sanitizer: DomSanitizer)), so no other Angular
// idiom conversion was needed.
// ============================================================
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { environment } from '../../../environments/environment';
import { WifiNetwork, ScopeConnectionStatus, ScopeState } from '../../@core/interfaces/device-integration/device-item.model';

const INITIAL_STATE: ScopeState = {
  wsStatus: 'disconnected',
  deviceConnected: false,
  previewRunning: false,
  status: 'disconnected',
};

@Injectable({
  providedIn: 'root',
})
export class ScopeManagerService {
  private ws: WebSocket | null = null;

  private readonly stateSubject = new BehaviorSubject<ScopeState>(INITIAL_STATE);
  readonly state$: Observable<ScopeState> = this.stateSubject.asObservable();

  // Live video frames get their own stream rather than living on ScopeState --
  // same reasoning already applied elsewhere in this project for high-frequency
  // data (X6's live ECG wave): a fast-changing binary stream doesn't belong in a
  // general state-change notifier. Each frame is wrapped in an object URL;
  // the previous URL is revoked on the next frame so blob URLs don't leak.
  //
  // FIX: exposed as SafeUrl, not a plain string. Angular's DomSanitizer
  // blocks blob: URLs from binding into [src] by default -- bypassSecurityTrustUrl()
  // marks each blob URL as trusted, safe here since we create every one ourselves
  // from bridge-supplied binary data, never from user/URL input.
  private readonly frameUrlSubject = new BehaviorSubject<SafeUrl | null>(null);
  readonly frameUrl$: Observable<SafeUrl | null> = this.frameUrlSubject.asObservable();
  private currentFrameUrl: string | null = null;

  // Kept alongside the object URL above so a caller can grab a still image --
  // the vendor bridge has no "take a snapshot" command of its own (it only
  // streams continuous preview frames), so the app-side "automatic image
  // capture" step (see TriageComponent.captureExamImageNow) works by
  // requesting the most recently received frame via captureSnapshot() below.
  private currentFrameBlob: Blob | null = null;

  // ─────────────────────────────────────────────
  // Video recording
  //
  // The bridge has no MediaStream/WebRTC path -- it pushes one complete JPEG
  // per binary WS message (see the class doc comment above). To produce an
  // actual video file client-side with zero SDK/bridge changes, every
  // incoming frame is drawn onto an offscreen (never attached to the DOM)
  // <canvas>, whose captureStream() gives a real MediaStream that
  // MediaRecorder can record like any other. Recording auto-starts the
  // moment the first real frame is drawn (i.e. the instant the live feed
  // would actually be visible to a consumer bound to frameUrl$), and stops
  // via finishExamSession()/discardExamSession() -- never a second, competing
  // trigger, so there is exactly one place a MediaRecorder can be created.
  // ─────────────────────────────────────────────
  private static readonly RECORDING_FPS = 10;
  private mediaRecorder: MediaRecorder | null = null;
  private recordingChunks: Blob[] = [];
  private recordingCanvas: HTMLCanvasElement | null = null;
  private recordingCtx: CanvasRenderingContext2D | null = null;
  private drawingFrame = false;

  private reconnectHandle: any = null;
  private reconnectAttempts = 0;
  private static readonly MAX_RECONNECT_DELAY_MS = 10000;
  private static readonly SCAN_TIMEOUT_MS = 20000;
  private scanTimeoutHandle: any = null;

  // Set on an explicit disconnect() call so the onclose handler doesn't try
  // to reconnect a connection the user deliberately tore down.
  private userDisconnected = false;

  // True once any WebSocket to the bridge has ever reached OPEN. Lets the
  // very first connection attempt say "Could not reach…" instead of the
  // misleading "Lost connection… Retrying" (there was nothing to lose yet).
  private hasEverConnected = false;

  constructor(private sanitizer: DomSanitizer) {}

  connect(): void {
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      return;
    }
    this.userDisconnected = false;
    this.openSocket();
  }

  private openSocket(): void {
    const url = environment.scopeBridgeWsUrl || 'ws://localhost:8080';
    console.log('[ScopeManager] Connecting to bridge at', url);
    this.setState({ ...this.stateSubject.value, wsStatus: 'connecting', errorMessage: undefined });

    let ws: WebSocket;
    try {
      ws = new WebSocket(url);
    } catch (e) {
      console.error('[ScopeManager] Failed to open WebSocket:', e);
      this.setState({ ...this.stateSubject.value, wsStatus: 'disconnected', errorMessage: String(e) });
      this.scheduleReconnect();
      return;
    }
    ws.binaryType = 'blob';
    this.ws = ws;

    // Every handler below checks `this.ws !== ws` before touching shared
    // state. disconnect() synchronously nulls `this.ws` and closes the
    // socket, but the browser's close event for it is always asynchronous --
    // if connect() is called again before that event fires, the OLD
    // socket's onclose still runs later and would otherwise null out the
    // NEW socket's reference and schedule a spurious reconnect on top of a
    // connection that's actually fine. Capturing `ws` in the closure and
    // comparing against `this.ws` is how every handler tells "am I still
    // the current socket" apart from "I'm a stale one whose real-world
    // counterpart is already gone."
    ws.onopen = () => {
      if (this.ws !== ws) return;
      console.log('[ScopeManager] Bridge connected');
      this.reconnectAttempts = 0;
      this.hasEverConnected = true;
      this.setState({ ...this.stateSubject.value, wsStatus: 'connected', errorMessage: undefined });
    };

    ws.onmessage = (event: MessageEvent) => {
      if (this.ws !== ws) return;
      this.handleMessage(event.data);
    };

    ws.onerror = (event) => {
      if (this.ws !== ws) return;
      console.error('[ScopeManager] WebSocket error:', event);
    };

    ws.onclose = () => {
      if (this.ws !== ws) return;
      console.warn('[ScopeManager] Bridge disconnected');
      this.ws = null;
      // A stale JPEG frame left on screen after the bridge itself drops is
      // indistinguishable from a genuinely live one -- clear it here too,
      // not just on an explicit stopPreview()/disconnect(), so a reconnect
      // never re-shows an old frame captioned as live before the first
      // fresh one arrives.
      this.clearFrame();
      this.setState({
        ...INITIAL_STATE,
        wsStatus: 'disconnected',
        errorMessage: this.userDisconnected
          ? undefined
          : this.hasEverConnected
            ? 'Lost connection to the scope-bridge process. Retrying…'
            : 'Could not reach the scope-bridge process. Retrying…',
      });
      if (!this.userDisconnected) {
        this.scheduleReconnect();
      }
    };
  }

  private scheduleReconnect(): void {
    if (this.reconnectHandle) return;
    this.reconnectAttempts++;
    const delay = Math.min(1000 * this.reconnectAttempts, ScopeManagerService.MAX_RECONNECT_DELAY_MS);
    console.log(`[ScopeManager] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`);
    this.reconnectHandle = setTimeout(() => {
      this.reconnectHandle = null;
      this.openSocket();
    }, delay);
  }

  private handleMessage(data: Blob | string): void {
    if (data instanceof Blob) {
      // Binary message = one complete JPEG frame. A WebSocket message Blob
      // always has type === '' (there's no MIME on the wire) -- re-wrap it
      // as image/jpeg here, once, for every consumer: without a declared
      // image/* type, a data: URL built from this blob (captureSnapshot() ->
      // FileReader.readAsDataURL(), used for exam-image stills) fails to
      // decode and renders as a broken image, even though the blob: URL
      // used for the live <img> feed below happens to still work (browsers
      // content-sniff blob: URLs instead of trusting their declared type).
      if (this.currentFrameUrl) {
        URL.revokeObjectURL(this.currentFrameUrl);
      }
      const isFirstFrameOfSession = !this.currentFrameBlob;
      const frameBlob = data.type === 'image/jpeg' ? data : new Blob([data], { type: 'image/jpeg' });
      this.currentFrameBlob = frameBlob;
      this.currentFrameUrl = URL.createObjectURL(frameBlob);
      this.frameUrlSubject.next(this.sanitizer.bypassSecurityTrustUrl(this.currentFrameUrl));

      // "The video stream is displayed" in practice means exactly this --
      // frameUrl$ is what every consumer binds into an <img> to show the
      // live feed, so the first frame to arrive while a preview is running
      // IS the moment it becomes visible. Auto-start recording right here,
      // with no separate button and no dependency on which component (if
      // any) is currently displaying the feed.
      if (this.stateSubject.value.previewRunning) {
        if (isFirstFrameOfSession) {
          console.log('[ScopeManager] Aabo Scope video displayed');
          console.log('[ScopeManager] Auto-starting video recording');
        }
        this.ensureRecording();
        this.drawFrameForRecording(data);
      }
      return;
    }

    let msg: any;
    try {
      msg = JSON.parse(data);
    } catch (e) {
      console.warn('[ScopeManager] Failed to parse message:', data);
      return;
    }

    switch (msg.type) {
      case 'wifi_list':
        console.log('[ScopeManager] WiFi scan result:', msg.networks?.length, 'network(s)');
        this.clearScanTimeout();
        this.setState({ ...this.stateSubject.value, wifiNetworks: msg.networks, scanning: false });
        break;

      case 'wifi_status':
        this.setState({
          ...this.stateSubject.value,
          wifiSsid: msg.ssid,
          onScopeWifi: msg.onScope,
          // A prior message may have set errorMessage (e.g. the bridge's
          // pre-connect "no secondary interface" warning) -- once we're
          // confirmed on the Scope's own WiFi, that's no longer the current
          // problem, so don't let it linger as a stale error indefinitely.
          errorMessage: msg.onScope ? undefined : this.stateSubject.value.errorMessage,
        });
        break;

      case 'connection':
        console.log('[ScopeManager] Connection status:', msg.isConnected, msg.deviceType);
        if (!msg.isConnected) {
          // Same staleness concern as onclose -- the device itself dropping
          // (bridge stays connected) leaves previewRunning correctly reset
          // to false right below, but without this the last frame stays on
          // screen looking live until the device reconnects and sends a
          // fresh one.
          this.clearFrame();
        }
        this.setState({
          ...this.stateSubject.value,
          deviceConnected: msg.isConnected,
          deviceType: msg.deviceType,
          previewRunning: msg.isConnected ? this.stateSubject.value.previewRunning : false,
          errorMessage: msg.isConnected ? undefined : this.stateSubject.value.errorMessage,
        });
        break;

      case 'battery':
        this.setState({ ...this.stateSubject.value, batteryPercent: msg.level });
        break;

      case 'error':
        console.error('[ScopeManager] Bridge error:', msg.message);
        // An error means whatever was in flight (a scan included) isn't
        // going to complete normally -- don't leave "Scanning…" stuck on
        // screen waiting for a wifi_list that was never coming.
        this.clearScanTimeout();
        this.setState({ ...this.stateSubject.value, errorMessage: msg.message, scanning: false });
        break;

      case 'network_status':
        this.setState({
          ...this.stateSubject.value,
          internetReachable: msg.internetReachable,
          hasSecondaryInterface: msg.hasSecondaryInterface,
        });
        break;

      case 'logs_info':
        // Not surfaced in the UI -- available if a log-management panel is added later.
        break;

      default:
        console.warn('[ScopeManager] Unknown message type:', msg.type);
    }
  }

  private send(payload: object): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.warn('[ScopeManager] Cannot send -- not connected to bridge:', payload);
      return;
    }
    this.ws.send(JSON.stringify(payload));
  }

  scanWifi(): void {
    console.log('[ScopeManager] Requesting WiFi scan');
    this.send({ cmd: 'scanWifi' });
    this.setState({ ...this.stateSubject.value, scanning: true, errorMessage: undefined });
    this.clearScanTimeout();
    this.scanTimeoutHandle = setTimeout(() => {
      this.scanTimeoutHandle = null;
      if (!this.stateSubject.value.scanning) return; // a result already arrived
      this.setState({
        ...this.stateSubject.value,
        scanning: false,
        errorMessage: 'WiFi scan timed out. Make sure the scope-bridge process is running and try again.',
      });
    }, ScopeManagerService.SCAN_TIMEOUT_MS);
  }

  private clearScanTimeout(): void {
    if (this.scanTimeoutHandle) {
      clearTimeout(this.scanTimeoutHandle);
      this.scanTimeoutHandle = null;
    }
  }

  /** Asks the bridge to re-measure internet reachability / secondary-interface presence right now, rather than waiting for its periodic broadcast. */
  checkNetwork(): void {
    this.send({ cmd: 'checkNetwork' });
  }

  connectWifi(ssid: string, password?: string): void {
    console.log('[ScopeManager] Requesting connect to WiFi:', ssid);
    this.send({ cmd: 'connectWifi', ssid, password });
  }

  startPreview(): void {
    console.log('[ScopeManager] Requesting start preview');
    console.log('[ScopeManager] Aabo Scope video stream initialized');
    this.send({ cmd: 'startPreview' });
    this.setState({ ...this.stateSubject.value, previewRunning: true });
  }

  stopPreview(): void {
    console.log('[ScopeManager] Requesting stop preview');
    // Safety net: a caller stopping preview directly (bypassing
    // finishExamSession()/discardExamSession()) must not leave a
    // MediaRecorder running against a canvas nobody is feeding anymore.
    // Harmless no-op if a recording was already stopped normally.
    this.discardRecording();
    this.send({ cmd: 'stopPreview' });
    this.setState({ ...this.stateSubject.value, previewRunning: false });
    this.clearFrame();
  }

  /** Revokes the current live-preview frame's object URL (if any) and clears it from both the blob cache (captureSnapshot()) and frameUrl$, so nothing keeps pointing at a frame that's no longer live. */
  private clearFrame(): void {
    if (this.currentFrameUrl) {
      URL.revokeObjectURL(this.currentFrameUrl);
      this.currentFrameUrl = null;
      this.currentFrameBlob = null;
      this.frameUrlSubject.next(null);
    }
  }

  /**
   * Returns the most recently received live-preview frame as a raw Blob, or
   * null if no frame has arrived yet. The vendor bridge has no snapshot
   * command of its own -- "capturing" an image means grabbing whatever the
   * continuous preview stream last delivered.
   */
  captureSnapshot(): Blob | null {
    return this.currentFrameBlob;
  }

  /**
   * Starts recording into an offscreen canvas fed from every incoming frame,
   * IF one isn't already running. Safe to call on every single frame --
   * the state check makes repeated calls (component re-renders, multiple
   * frames arriving before the first start completes) a no-op, so exactly
   * one MediaRecorder ever exists per session.
   */
  private ensureRecording(): void {
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') return; // already recording -- dedupe guard
    if (!this.recordingCanvas) {
      this.recordingCanvas = document.createElement('canvas');
      this.recordingCanvas.width = 640;
      this.recordingCanvas.height = 480;
      this.recordingCtx = this.recordingCanvas.getContext('2d');
    }
    let stream: MediaStream;
    try {
      stream = this.recordingCanvas.captureStream(ScopeManagerService.RECORDING_FPS);
    } catch (e) {
      console.error('[ScopeManager] canvas.captureStream() failed -- cannot record:', e);
      return;
    }
    let mimeType = 'video/webm;codecs=vp9';
    if (!MediaRecorder.isTypeSupported(mimeType)) mimeType = 'video/webm;codecs=vp8';
    if (!MediaRecorder.isTypeSupported(mimeType)) mimeType = 'video/webm';
    this.recordingChunks = [];
    try {
      this.mediaRecorder = new MediaRecorder(stream, { mimeType });
    } catch (e) {
      console.error('[ScopeManager] Failed to create MediaRecorder:', e);
      return;
    }
    this.mediaRecorder.ondataavailable = (e: BlobEvent) => {
      if (e.data && e.data.size > 0) this.recordingChunks.push(e.data);
    };
    this.mediaRecorder.onerror = (e) => console.error('[ScopeManager] MediaRecorder error:', e);
    this.mediaRecorder.start(1000); // gather a chunk every second so a crash doesn't lose the whole recording
    console.log('[ScopeManager] Video recording started');
    this.setState({ ...this.stateSubject.value, recording: true });
  }

  /** Draws one JPEG frame onto the recording canvas. Drops overlapping frames (decode-in-progress) rather than queuing -- at RECORDING_FPS this is imperceptible and avoids unbounded backpressure if frames arrive faster than they can be decoded. */
  private drawFrameForRecording(blob: Blob): void {
    if (this.drawingFrame || !this.recordingCtx || !this.recordingCanvas) return;
    this.drawingFrame = true;
    createImageBitmap(blob).then(bitmap => {
      const canvas = this.recordingCanvas;
      if (canvas && (canvas.width !== bitmap.width || canvas.height !== bitmap.height)) {
        canvas.width = bitmap.width;
        canvas.height = bitmap.height;
      }
      this.recordingCtx?.drawImage(bitmap, 0, 0);
      bitmap.close();
      this.drawingFrame = false;
    }).catch(err => {
      console.error('[ScopeManager] Failed to draw frame for recording:', err);
      this.drawingFrame = false;
    });
  }

  /** Stops the active recording (if any) and resolves with the assembled video Blob, or null if nothing was recording. */
  private stopRecordingAndGetBlob(): Promise<Blob | null> {
    return new Promise(resolve => {
      const recorder = this.mediaRecorder;
      if (!recorder || recorder.state === 'inactive') { resolve(null); return; }
      console.log('[ScopeManager] Stopping video recording');
      const mimeType = recorder.mimeType || 'video/webm';
      recorder.onstop = () => {
        const blob = new Blob(this.recordingChunks, { type: mimeType });
        console.log('[ScopeManager] Video blob generated:', blob.size, 'bytes');
        this.recordingChunks = [];
        this.mediaRecorder = null;
        this.setState({ ...this.stateSubject.value, recording: false });
        resolve(blob);
      };
      recorder.stop();
    });
  }

  /** Stops the active recording (if any) and discards whatever was captured -- used when a session is skipped/cancelled rather than finished, so nothing is saved for a step the clinician didn't complete. */
  private discardRecording(): void {
    const recorder = this.mediaRecorder;
    if (!recorder || recorder.state === 'inactive') return;
    recorder.ondataavailable = null;
    recorder.onstop = null;
    try { recorder.stop(); } catch { /* already stopping */ }
    this.recordingChunks = [];
    this.mediaRecorder = null;
    this.setState({ ...this.stateSubject.value, recording: false });
  }

  /** The "Finish examination" action: stops recording, saves the video, and stops the camera preview. Returns the recorded video Blob, or null if nothing was recorded. */
  async finishExamSession(): Promise<Blob | null> {
    const blob = await this.stopRecordingAndGetBlob();
    this.stopPreview();
    return blob;
  }

  /** Stops recording (discarding it) and the camera preview together -- used when a session is skipped/cancelled rather than finished. */
  discardExamSession(): void {
    this.discardRecording();
    this.stopPreview();
  }

  disconnect(): void {
    console.log('[ScopeManager] Disconnect requested by user');
    this.userDisconnected = true;
    this.clearScanTimeout();
    // Deliberately does NOT touch the recorder here. BehaviorSubject.next()
    // below calls every state$ subscriber SYNCHRONOUSLY, before this method
    // returns -- TriageComponent's onScopeState() is one of them, and reacts
    // to "device just went from connected to not" by awaiting
    // scope.finishExamSession() to save whatever was recorded (recording
    // lifecycle requirement: stop and save on disconnect, don't discard it).
    // That await suspends on a later microtask, so if this method discarded
    // the recorder itself first, the save would find nothing left to save --
    // an earlier version of this method did exactly that and silently lost
    // every recording made when the doctor disconnected Aabo Scope instead
    // of clicking Finish first. If nothing was actually recording, the
    // MediaRecorder is already null/inactive and there's nothing to clean up.
    if (this.reconnectHandle) {
      clearTimeout(this.reconnectHandle);
      this.reconnectHandle = null;
    }
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.clearFrame();
    this.setState({ ...INITIAL_STATE });
  }

  private setState(state: ScopeState): void {
    this.stateSubject.next({ ...state, status: this.deriveStatus(state) });
  }

  /**
   * Single source of truth for "what state are we in" -- every caller passes
   * the raw fields (wsStatus/deviceConnected/scanning/etc.) into setState()
   * and this derives the one canonical status from them, so the status card
   * and every other consumer can never disagree about it.
   */
  private deriveStatus(s: ScopeState): ScopeConnectionStatus {
    if (s.wsStatus === 'connecting') return 'connecting';
    if (s.wsStatus === 'disconnected') return s.errorMessage ? 'error' : 'disconnected';
    // wsStatus === 'connected' (bridge reachable) from here on.
    if (s.errorMessage) return 'error';
    if (s.deviceConnected) return s.recording ? 'capturing' : 'connected';
    if (s.scanning) return 'discovering';
    if (s.onScopeWifi) return 'device_found'; // on the device's own WiFi, its UDP handshake hasn't landed yet
    if (s.wifiNetworks?.some(n => n.isScopeNetwork)) return 'device_found'; // scan found it, not yet connected
    return 'disconnected';
  }
}
